import type { StyleLibrary } from "schemas/generated/project";
import type { ColorPreset } from "./utils/presets";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowLeft, Check } from "lucide-react";

import { colorPresets } from "./utils/presets";

interface PresetOptionProps {
  presetData: ColorPreset["preset1"] | ColorPreset["preset2"];
  isSelected: boolean;
  onSelect: () => void;
}

const PresetOption: React.FC<PresetOptionProps> = ({
  presetData,
  isSelected,
  onSelect,
}) => {
  return (
    <div
      className={twMerge(
        "flex-1 rounded-lg border p-2 gap-2 flex flex-col cursor-pointer relative",
        isSelected ? "border-black border-2" : "border-border",
      )}
      onClick={onSelect}
    >
      {isSelected && (
        <div className="absolute -top-3 -right-3 z-10 w-6 h-6 bg-black rounded-full flex items-center justify-center">
          <Check size={16} className="text-white" />
        </div>
      )}
      <div
        className="rounded-lg p-2 gap-3 flex flex-row"
        style={{ backgroundColor: presetData.light }}
      >
        <div className="w-1/2 flex items-start justify-center p-1 gap-1 flex-col">
          <div className="flex items-center gap-1">
            <div className="flex">
              <span
                className="text-[3px]"
                style={{ color: presetData.primary }}
              >
                ★
              </span>
              <span
                className="text-[3px]"
                style={{ color: presetData.primary }}
              >
                ★
              </span>
              <span
                className="text-[3px]"
                style={{ color: presetData.primary }}
              >
                ★
              </span>
              <span
                className="text-[3px]"
                style={{ color: presetData.primary }}
              >
                ★
              </span>
              <span
                className="text-[3px]"
                style={{ color: presetData.primary }}
              >
                ★
              </span>
            </div>
            <span className="text-[3px]" style={{ color: presetData.muted }}>
              4.8 88,888+ Trusted Customer Reviews
            </span>
          </div>
          <span
            className="text-[9px] font-bold"
            style={{ color: presetData.dark }}
          >
            Plant the Future, One Leaf at a Time.
          </span>
          <span
            className="text-[3px] font-normal"
            style={{ color: presetData.muted }}
          >
            This is a short description that provides context for this section.
            Keep it brief and informative.
          </span>
          <Button
            variant="primary"
            size="sm"
            UNSAFE_className="w-[33px] h-[7px] text-[4px] rounded-[1px]"
            style={{
              backgroundColor: presetData.primary,
              borderColor: presetData.primary,
            }}
          >
            Button
          </Button>
        </div>
        <div
          className="h-[90px] w-1/2 rounded-sm"
          style={{ backgroundColor: presetData.subtle }}
        />
      </div>
      <div className="flex h-4 relative">
        <div
          className="flex-1 h-4 rounded-full -mr-3 z-[5] relative"
          style={{ backgroundColor: presetData.light }}
        />
        <div
          className="flex-1 h-4 rounded-full -mr-3 z-[4] relative"
          style={{ backgroundColor: presetData.subtle }}
        />
        <div
          className="flex-1 h-4 rounded-full -mr-3 z-[3] relative"
          style={{ backgroundColor: presetData.muted }}
        />
        <div
          className="flex-1 h-4 rounded-full -mr-3 z-[2] relative"
          style={{ backgroundColor: presetData.dark }}
        />
        <div
          className="flex-1 h-4 rounded-full z-[1] relative"
          style={{ backgroundColor: presetData.primary }}
        />
      </div>
    </div>
  );
};

interface PresetBlockProps {
  preset: ColorPreset;
  presetIndex: number;
  selectedPreset: { presetIndex: number; subPreset: 1 | 2 } | null;
  onPresetSelect: (presetIndex: number, subPreset: 1 | 2) => void;
}

const PresetBlock: React.FC<PresetBlockProps> = ({
  preset,
  presetIndex,
  selectedPreset,
  onPresetSelect,
}) => {
  const isPreset1Selected =
    selectedPreset?.presetIndex === presetIndex &&
    selectedPreset?.subPreset === 1;
  const isPreset2Selected =
    selectedPreset?.presetIndex === presetIndex &&
    selectedPreset?.subPreset === 2;

  return (
    <div className="w-full flex flex-col flex-shrink-0">
      <div className="text-sm text-muted">{preset.title}</div>
      <div className="flex gap-2 mt-2">
        <PresetOption
          presetData={preset.preset1}
          isSelected={isPreset1Selected}
          onSelect={() => onPresetSelect(presetIndex, 1)}
        />
        <PresetOption
          presetData={preset.preset2}
          isSelected={isPreset2Selected}
          onSelect={() => onPresetSelect(presetIndex, 2)}
        />
      </div>
    </div>
  );
};

interface DesignLibraryColorPresetsProps {
  styleLibrary: StyleLibrary | null;
  onBack?: () => void;
  onStyleLibraryChange: (styleLibrary: StyleLibrary) => void;
}

export const DesignLibraryColorPresets: React.FC<
  DesignLibraryColorPresetsProps
> = ({ styleLibrary, onBack, onStyleLibraryChange }) => {
  const [selectedPreset, setSelectedPreset] = React.useState<{
    presetIndex: number;
    subPreset: 1 | 2;
  } | null>(null);

  const handlePresetSelect = (presetIndex: number, subPreset: 1 | 2) => {
    if (!styleLibrary) {
      return;
    }

    const preset = colorPresets[presetIndex];
    if (!preset) {
      return;
    }

    const selectedSubPreset = subPreset === 1 ? preset.preset1 : preset.preset2;

    setSelectedPreset({ presetIndex, subPreset });

    const updatedColors = {
      neutrals: {
        light: selectedSubPreset.light,
        subtle: selectedSubPreset.subtle,
        muted: selectedSubPreset.muted,
        dark: selectedSubPreset.dark,
      },
      brand: {
        primary: { color: selectedSubPreset.primary, name: "Primary" },
        secondary: { color: selectedSubPreset.secondary, name: "Secondary" },
        tertiary: { color: "", name: "" },
        accent: { color: "", name: "" },
      },
    };

    onStyleLibraryChange({
      ...styleLibrary,
      colors: updatedColors,
    });
  };

  return (
    <div className="flex flex-col items-start flex-[1_0_0] self-stretch min-h-0 rounded-xl border-0.5 border-border bg-white shadow-sm">
      <div className="flex justify-between items-center self-stretch p-6">
        <div className="flex items-center gap-3">
          <Button
            icon
            variant="tertiary"
            size="sm"
            onClick={() => {
              onBack?.();
            }}
          >
            <ArrowLeft />
          </Button>
          <div className="text-base font-semibold">Color Presets</div>
        </div>
      </div>
      <div className="flex flex-1 flex-col self-stretch overflow-y-auto no-scrollbar p-6 pt-0 gap-6">
        {colorPresets.map((preset, index) => (
          <PresetBlock
            key={index}
            preset={preset}
            presetIndex={index}
            selectedPreset={selectedPreset}
            onPresetSelect={handlePresetSelect}
          />
        ))}
      </div>
    </div>
  );
};
